import { InvalidCatalogDefinitionError } from "./errors.model";
import { assignMemberIds, highWaterMark, type MemberId, memberIdSequence, memberIds } from "./member-identity";
import { CatalogDefinition, type CatalogSpine } from "./spine.model";
import type { ChoiceCardinality, ChoiceOption, DiagnosticComponentValue } from "./value.model";
import { validateValue } from "./value.model";

// ============================================================
// FindingDefinition — one clinical judgement, defined once for the whole catalog
//
//   FindingDefinition
//   ├── (spine)
//   └── kind
//       ├── Choice:   cardinality · offerOther
//       │             options[]       ≥1, at most one marked normal
//       └── Measured: components[]    ≥1
//
// THE REGISTRY OWNS CONTENT; AN EXAM OWNS ARRANGEMENT. Everything here used to
// live inline in `ExamDefinition.sections[].findings[]`, where it had no
// identity outside that one exam — so a composite spanning three exams, a
// cross-visit trend, and "can this diagnostic answer this finding" were all
// inexpressible. An exam now REFERENCES a finding and cannot edit it, not even
// as a delta: the moment an exam can retitle a finding or narrow its options
// there are partial copies and the registry is authoritative for nothing.
//
// The canonical url identifies the lineage. A semantic change mints a new
// lineage and deprecates the old one; recorded answers retain their references.
//
// OPTION IDENTITIES ARE SYSTEM-ASSIGNED, minted here. That is what lets
// `ExamDefinition` drop its own finding and option marks: the identities
// recorded answers cite now belong to the finding, which is the aggregate that
// actually owns them.
//
// FINDINGS DEPRECATE, NEVER DELETE — `deactivate()` from the spine, and a
// finding with a single recorded answer is permanent. Deactivating is never
// blocked whatever references it; a consuming exam surfaces the retirement and
// assembly refuses it by name.
// ============================================================

const OPTION_PREFIX = "opt";
const COMPONENT_PREFIX = "cmp";

// NORMAL IS A DESIGNATION ON ONE OF THE FINDING'S OWN OPTIONS, not a reserved
// extra response: a reflex's normal is "2+", strength's is "5". Option-level
// rather than a definition-level id reference because an authored option has
// no identity until this aggregate mints it, so a reference could not name it.
// On a multiple-choice finding the normal option is exclusive — see `Finding`.

/** An authored option, which may be the finding's one normal result. */
export type FindingOptionInput = ChoiceOption & { normal?: boolean };

/** An option the aggregate has seen, so its identity is settled. `normal` is present only when true. */
export type FindingOption = ChoiceOption & { readonly id: MemberId; normal?: true };

export interface FindingComponentInput {
  id?: MemberId;
  label: string;
  value: DiagnosticComponentValue;
}

export interface FindingComponent extends FindingComponentInput {
  id: MemberId;
}

/**
 * The answer shape, which is the whole of what a revision can change. Declared
 * once over its member types: settled content holds identified members, input
 * may still carry members the aggregate has not seen.
 */
type FindingContentOf<Option, Component> =
  | {
      kind: "Choice";
      cardinality: ChoiceCardinality;
      /** Whether "other" is offered — the escape hatch that makes choice-only tolerable. */
      offerOther: boolean;
      options: readonly Option[];
    }
  | {
      kind: "Measured";
      components: readonly Component[];
    };

export type FindingContent = FindingContentOf<FindingOption, FindingComponent>;
export type ChoiceFindingContent = Extract<FindingContent, { kind: "Choice" }>;
export type MeasuredFindingContent = Extract<FindingContent, { kind: "Measured" }>;

/** Other defaults to offered. */
type DefaultedChoiceFlag = "offerOther";
type WithDefaultedFlags<Content> = Content extends { kind: "Choice" }
  ? Omit<Content, DefaultedChoiceFlag> & Partial<Pick<Content, Extract<keyof Content, DefaultedChoiceFlag>>>
  : Content;

export type FindingContentInput = WithDefaultedFlags<FindingContentOf<FindingOptionInput, FindingComponentInput>>;

/** Identity high-water marks, persisted so removed options and components are never reused. */
export interface FindingMemberIdMarks {
  options: number;
  components: number;
}

export type FindingDefinitionFacts = FindingContent & { spine: CatalogSpine; marks: FindingMemberIdMarks };

/**
 * A stored revision as persistence actually holds it: a row written before the
 * mark was persisted carries none. The constructor already treats an absent
 * mark as a sequence seeded from the members present, so nothing else changes.
 */
export type StoredFindingDefinitionProps = FindingContent & { spine: CatalogSpine; marks?: FindingMemberIdMarks };

export type FindingDefinitionInput = FindingContentInput & {
  spine: CatalogSpine;
  /** Supplied when rehydrating. Absent on a brand new lineage, whose sequence starts at zero. */
  marks?: FindingMemberIdMarks;
};

export class FindingDefinition extends CatalogDefinition<FindingDefinition> {
  private current: FindingContent;
  private marks: FindingMemberIdMarks;

  constructor(input: FindingDefinitionInput) {
    super(input.spine);
    // Seeded from the payload as well as the stored mark, for the same reason
    // `ExamDefinition` does it: a caller handing in identified options without
    // their mark would otherwise start minting at one and collide on the first
    // new option. The stored mark still wins when it is ahead, because
    // `highWaterMark` cannot see a removed option.
    this.marks = {
      options: Math.max(input.marks?.options ?? 0, highWaterMark(contentOptions(input))),
      components: Math.max(input.marks?.components ?? 0, highWaterMark(contentComponents(input))),
    };
    const adopted = this.adopt(input, memberIds(contentOptions(input)), memberIds(contentComponents(input)));
    this.current = adopted.content;
    this.marks = adopted.marks;
  }

  get kind(): FindingContent["kind"] {
    return this.current.kind;
  }

  /** The answer shape. Narrow on `kind` to reach a choice's options or a measurement's components. */
  get content(): FindingContent {
    return structuredClone(this.current);
  }

  get memberIdMarks(): FindingMemberIdMarks {
    return { ...this.marks };
  }

  snapshot(): FindingDefinitionFacts {
    return this.facts;
  }

  get facts(): FindingDefinitionFacts {
    return {
      spine: this.spineFacts,
      ...structuredClone(this.current),
      marks: this.memberIdMarks,
    };
  }

  protected copyForRevision(spine: CatalogSpine): FindingDefinition {
    return new FindingDefinition({
      spine,
      ...structuredClone(this.current),
      marks: { ...this.marks },
    });
  }

  protected substantiveFacts(): unknown {
    return { ...this.current };
  }

  /**
   * Replace the answer shape.
   *
   * A semantic change mints a new lineage instead.
   *
   * Option identities are carried, never reminted — see `assignMemberIds`.
   */
  setContent(content: FindingContentInput): void {
    const adopted = this.adopt(
      content,
      memberIds(contentOptions(this.current)),
      memberIds(contentComponents(this.current)),
    );
    this.current = adopted.content;
    this.marks = adopted.marks;
  }

  private adopt(
    incoming: FindingContentInput,
    known: ReadonlySet<MemberId>,
    knownComponents: ReadonlySet<MemberId>,
  ): { content: FindingContent; marks: FindingMemberIdMarks } {
    const sequence = memberIdSequence(OPTION_PREFIX, this.marks.options);
    if (incoming.kind === "Measured") {
      if (!incoming.components.length)
        throw new InvalidCatalogDefinitionError("A measured finding requires components");
      const labels = new Set<string>();
      const componentSequence = memberIdSequence(COMPONENT_PREFIX, this.marks.components);
      const components = assignMemberIds(incoming.components, knownComponents, componentSequence, "component").map(
        (component) => {
          if (!component.label.trim() || labels.has(component.label))
            throw new InvalidCatalogDefinitionError("Finding component labels must be nonblank and unique");
          labels.add(component.label);
          const value =
            component.value.kind === "Choice"
              ? { ...component.value, options: assignMemberIds(component.value.options, known, sequence, "option") }
              : structuredClone(component.value);
          validateValue(value, { collection: component.label });
          return { id: identified(component.id), label: component.label, value };
        },
      );
      const content: FindingContent = { kind: "Measured", components };
      this.validate(content);
      return { content, marks: { options: sequence.high, components: componentSequence.high } };
    }
    const options = assignMemberIds(incoming.options, known, sequence, "option").map(
      ({ normal, ...option }): FindingOption => ({
        ...option,
        id: identified(option.id),
        ...(normal ? { normal } : {}),
      }),
    );
    const content: FindingContent = {
      kind: "Choice",
      cardinality: incoming.cardinality,
      offerOther: incoming.offerOther ?? true,
      options,
    };
    this.validate(content);
    return { content, marks: { options: sequence.high, components: this.marks.components } };
  }

  private validate(content: FindingContent): void {
    if (content.kind === "Measured") return;
    // The finding IS a choice, so the choice value object's rules — at least
    // one option, every option labelled, no duplicate identities — are applied
    // rather than restated. `allowFreeText` is false because free text rides on
    // the `other` response and the note, never on the option set.
    validateValue(
      { kind: "Choice", options: content.options, cardinality: content.cardinality, allowFreeText: false },
      { collection: "option" },
    );
    if (content.options.filter((option) => option.normal).length > 1)
      throw new InvalidCatalogDefinitionError("A finding marks at most one option as normal");
  }
}

function contentOptions(content: FindingContentInput): readonly ChoiceOption[] {
  return content.kind === "Measured"
    ? content.components.flatMap((component) => (component.value.kind === "Choice" ? component.value.options : []))
    : content.options;
}

function contentComponents(content: FindingContentInput): readonly FindingComponentInput[] {
  return content.kind === "Measured" ? content.components : [];
}

/** `assignMemberIds` always returns an identified member; this is where that guarantee is stated. */
function identified(id: MemberId | undefined): MemberId {
  if (id === undefined) {
    throw new InvalidCatalogDefinitionError("A finding member was not assigned an identity");
  }
  return id;
}
